import { computed, watch } from "vue";
import { t } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean } from "#ce/utils/props";

function isDisabledItem(item) {
  return toBoolean(item?.disabled) || toBoolean(item?.props?.disabled);
}

// Every item with its nested `children`, depth first.
function submissionItems(candidates) {
  return candidates.flatMap((item) => [item, ...(Array.isArray(item?.children) ? submissionItems(item.children) : [])]);
}

/**
 * Mirrors the selection into the form, as a native select submits it: one string while single,
 * one entry per selected value under `multiple`, and nothing for a disabled item. Under `multiple`
 * each value submits once, in item order, with values no item matches (custom entries) after them
 * in the order they were added. A selected prompt row submits "". A required select is missing a
 * value until something other than the prompt or a placeholder row is selected.
 *
 * Each selected entry's item is remembered once the list resolves it, along with the default
 * selection's, so a `return-object` pick and a disabled pick stay resolvable after `items` is
 * replaced under them.
 *
 * @param {object} props  Owning component's reactive props; reads `prompt` and `required`.
 * @param {{ model: import("vue").Ref<any[]>, items: import("vue").Ref<any[]>,
 *   defaultValue: import("vue").Ref<any>, multiple: import("vue").Ref<boolean>,
 *   initialModel: () => any[], findItem: (value: any) => any, getItemValue: (item: any) => any,
 *   form: ReturnType<typeof import("#ce/composables/useForm").useForm>,
 *   anchor: import("vue").Ref<HTMLElement | null> }} options  `form` is the host's `useForm()`.
 *   `anchor` is the element the validation bubble points at.
 * @returns {{ resolvedItem: (value: any) => any, hasSelection: import("vue").ComputedRef<boolean> }}
 */
export function useSelectSubmission(
  props,
  { model, items, defaultValue, multiple, initialModel, findItem, getItemValue, form, anchor },
) {
  const enabledItems = computed(() => submissionItems(items.value).filter((item) => !isDisabledItem(item)));

  const resolvedItems = new Map();

  // Flushed synchronously so a write and a replacement of `items` in the same turn still cache
  // against the list the write was made against.
  watch([model, items, defaultValue], cacheResolvedItems, {
    immediate: true,
    flush: "sync",
  });

  const formValue = computed(() => {
    const values = model.value.filter((entry) => entry !== null && entry !== undefined);

    if (!multiple.value) {
      if (!values.length) {
        return model.value.includes(null) && props.prompt ? "" : null;
      }

      return isDisabledSelection(values[0]) ? null : String(values[0]);
    }

    if (!form.name.value) {
      return null;
    }

    const data = new FormData();
    submissionValues(values).forEach((entry) => data.append(form.name.value, String(entry)));

    return data;
  });

  form.mirrorFormValue(formValue);

  const isRequired = computed(() => isPresent(props.required));
  const hasSelection = computed(() =>
    model.value.some(
      (entry) =>
        entry !== null && entry !== undefined && (multiple.value || entry !== "" || !resolvedItem(entry)?.placeholder),
    ),
  );

  form.mirrorValueMissing(
    () => isRequired.value && !hasSelection.value,
    () => t("select_value_missing"),
    anchor,
  );

  function findSubmissionItem(value) {
    return enabledItems.value.find((item) => String(getItemValue(item)) === String(value)) ?? findItem(value);
  }

  function isDisabledSelection(value) {
    return isDisabledItem(findSubmissionItem(value) ?? resolvedItems.get(String(value)));
  }

  function submissionValues(values) {
    const remaining = new Map();

    for (const entry of values) {
      const key = String(entry);

      if (!isDisabledSelection(entry) && !remaining.has(key)) {
        remaining.set(key, entry);
      }
    }

    const ordered = [];

    for (const item of enabledItems.value) {
      const entry = getItemValue(item);

      if (remaining.delete(String(entry))) {
        ordered.push(entry);
      }
    }

    return [...ordered, ...remaining.values()];
  }

  // The display keeps reading `findItem` itself, whose unresolved title is what tells the field to
  // hold the user's text.
  function resolvedItem(value) {
    return findItem(value) ?? resolvedItems.get(String(value));
  }

  function cacheResolvedItems(selectionStates) {
    const [entries] = selectionStates;

    const retainedEntries = [...entries, ...initialModel()];
    const keys = new Set(retainedEntries.map(String));

    resolvedItems.forEach((_, key) => {
      if (!keys.has(key)) {
        resolvedItems.delete(key);
      }
    });

    retainedEntries.forEach((entry) => {
      const found = findSubmissionItem(entry);

      if (found !== undefined) {
        resolvedItems.set(String(entry), found);
      }
    });
  }

  return {
    resolvedItem,
    hasSelection,
  };
}
